// BoardingCheckoutCard.jsx
import { useEffect, useState, useRef } from "react";
import { FaEye, FaInfoCircle } from "react-icons/fa";
import CheckoutItemTable from "./CheckoutItemTable";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";

const BoardingCheckoutCard = ({
  service,
  pet,
  getPetIcon,
  onUpdate,
  onCancel,
  clientId,
  onViewActivity,
  isOrderClosed,
}) => {
  // ---------- Local state (UI values) ----------
  const [serviceId, setServiceId] = useState(service.service_id || "");
  const [roomTypeId, setRoomTypeId] = useState(service.room_type_id || "");
  const [roomId, setRoomId] = useState(service.room_id || "");

  // ---------- Other state ----------
  const [boardingServices, setBoardingServices] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const breakdownRef = useRef(null);
  const lastPriceParamsRef = useRef("");
  let serviceStatus = "";

  switch (String(String(service.status || "").toLowerCase().trim() || "").toLowerCase().trim()) {
    case "cancelled":
    case "canceled":
    case "canceled":
    case "no show":
      serviceStatus = "Cancelled";
      break;

    case "started":
    case "checkin":
    case "check_in":
    case "in_progress":
      serviceStatus = "Started";
      break;

    case "completed":
    case "complete":
      serviceStatus = "Completed";
      break;

    case "closed":
      serviceStatus = "Closed";
      break;

    case "checkout":
    case "checked_out":
      serviceStatus = "Checked Out";
      break;

    case "confirmed":
      serviceStatus = service.isqueue === 0 ? "Scheduled" : "Queue";
      break;

    case "booked":
      serviceStatus = "Booked";
      break;

    default:
      serviceStatus = service.status
        ? String(service.status).replace(/_/g, " ")
        : "";
      break;
  }

  // 🔥 Add this ref to guard initial mount
  const isInitialMount = useRef(true);

  const selectedService = boardingServices.find((s) => s._id === serviceId);
  const selectedRoomType = roomTypes.find((rt) => rt._id === roomTypeId);
  const selectedRoom = availableRooms.find((r) => r._id === roomId);

  const nights =
    service.checkin && service.checkout
      ? Math.max(
          1,
          Math.ceil(
            (new Date(service.checkout) - new Date(service.checkin)) / 86400000,
          ),
        )
      : 1;

  const fmt = (v) => (Number(v) || 0).toFixed(0);


  // ---------- Fetch boarding services ----------
  useEffect(() => {
    const fetchBoardingServices = async () => {
      try {
        const res = await httpService.get(
          `/vendorroomservice/getRoomServices?status=active&petType=${pet.petType}`,
        );
        setBoardingServices(res?.data || []);
      } catch (err) {
        console.error("Error fetching boarding services:", err);
      }
    };
    fetchBoardingServices();
  }, [pet.petType]);

  // ---------- Room types ----------
  useEffect(() => {
    if (
      serviceId &&
      service.checkin &&
      service.checkout &&
      pet.petType &&
      boardingServices.length > 0
    ) {
      setLoadingRoomTypes(true);
      httpService
        .post("/room/getAvailableVendorRooms", {
          checkIn: service.checkin,
          checkOut: service.checkout,
          type: "boarding",
          petType: pet.petType,
          serviceId: serviceId,
        })
        .then((res) => {
          const allowedRoomTypeIds = new Set(
            selectedService.room_type_surcharges?.options?.map(
              (option) => option.room_type_id,
            ) || [],
          );
          const filteredRooms = (res.data || []).filter((room) =>
            allowedRoomTypeIds.has(room._id),
          );
          setRoomTypes(filteredRooms);
          // If current roomTypeId is not in filtered list, reset it
          if (
            roomTypeId &&
            !filteredRooms.some((rt) => rt._id === roomTypeId)
          ) {
            setRoomTypeId("");
            setRoomId("");
            // ⏭️ Only call onUpdate if not initial mount
            if (!isInitialMount.current) {
              onUpdate({ ...service, room_type_id: "", room_id: "" });
            }
          }
        })
        .catch(() => toast.error("Failed to load room types"))
        .finally(() => setLoadingRoomTypes(false));
    } else {
      setRoomTypes([]);
    }
  }, [
    boardingServices,
    serviceId,
    service.checkin,
    service.checkout,
    pet.petType,
  ]);

  // ---------- Available rooms ----------
  useEffect(() => {
    if (roomTypeId && service.checkin && service.checkout && pet.petType) {
      setLoadingRooms(true);
      const payload = {
        checkIn: service.checkin,
        checkOut: service.checkout,
        type: "boarding",
        excludingBoardingId: service?.ref_id || null,
        petType: pet?.petType || "",
      };
      httpService
        .post(`/room/getAvailableVendorRooms`, payload)
        .then((res) => {
          const roomData = res.data?.find((item) => item._id === roomTypeId);
          setAvailableRooms(roomData?.available_room_numbers || []);
          // If current roomId not in list, reset
          if (
            roomId &&
            !roomData?.available_room_numbers?.some((r) => r._id === roomId)
          ) {
            setRoomId("");
            // ⏭️ Only call onUpdate if not initial mount
            if (!isInitialMount.current) {
              onUpdate({ ...service, room_id: "" });
            }
          }
        })
        .catch(() => toast.error("Failed to load rooms"))
        .finally(() => setLoadingRooms(false));
    } else {
      setAvailableRooms([]);
    }
  }, [roomTypeId, service.checkin, service.checkout, pet.petType]);

  // ---------- Price calculation API ----------
  useEffect(() => {
    // 🚫 Skip on initial mount to avoid triggering onUpdate
    if (isInitialMount.current) return;

    // Skip if missing required fields
    if (!clientId || !pet?.id || !serviceId || !roomTypeId) return;
    if (!service.checkin || !service.checkout) return;

    const paramsKey = `${clientId}_${pet.id}_${serviceId}_${roomTypeId}_${service.checkin}_${service.checkout}`;
    // Keep the stored order/billing total on first paint. Reprice only when
    // the user actually changes stay dates, room, or service.
    if (lastPriceParamsRef.current === "") {
      lastPriceParamsRef.current = paramsKey;
      return;
    }
    if (lastPriceParamsRef.current === paramsKey) return;
    lastPriceParamsRef.current = paramsKey;

    setPriceLoading(true);

    const payload = {
      client_id: clientId,
      pet_id: pet.id,
      room_service_id: serviceId,
      room_type_id: roomTypeId,
      checkin_date: service.checkin,
      checkout_date: service.checkout,
    };

    httpService
      .post("/vendorbooking/getPriceCalculation?service_type=boarding", payload)
      .then((response) => {
        let price = 0;
        let salePrice = 0;
        let taxAmount = 0;
        let taxPercentage = 0;
        let discountAmount = 0;
        let surchargeDetails = [];
        let surchargesTotal = 0;

        if (typeof response.data === "number") {
          price = response.data;
          salePrice = response.data;
        } else if (response.data) {
          price = response.data.price || 0;
          salePrice = response.data.sale_price || price;
          taxAmount =
            response.data.discounted_tax || response.data.taxAmount || 0;
          taxPercentage = response.data.taxPercentage || 0;
          discountAmount = response.data.discount_amount || 0;
          surchargeDetails = response.data.surchargeDetails || [];
          surchargesTotal = surchargeDetails.reduce(
            (sum, s) => sum + (s.price || 0),
            0,
          );
        }

        const totalPrice = salePrice + surchargesTotal + taxAmount;
        const unitPrice = nights > 0 ? totalPrice / nights : totalPrice;

        const updatedService = {
          ...service,
          serviceId,
          serviceName: selectedService?.name || service.serviceName,
          roomTypeId,
          roomType: selectedRoomType?.room_name || service.roomType,
          roomId,
          roomNumber: selectedRoom?.room_number || service.roomNumber,
          price: totalPrice,
          unit_price: unitPrice,
          tax_percentage: taxPercentage,
          gst_amount: taxAmount,
          discount_amount: discountAmount,
          sale_price: salePrice,
          surchargeDetails,
          surchargesTotal,
          originalPrice: service.originalPrice ?? service.price,
          service_type_name: "boarding",
        };

        onUpdate(updatedService);
      })
      .catch((error) => {
        console.error("Error calculating boarding price:", error);
        toast.error("Failed to calculate price");
        const fallbackPrice =
          Number(selectedService?.mrp_price) ||
          Number(selectedService?.basic_price) ||
          service.price ||
          0;

        const updatedService = {
          ...service,
          serviceId,
          serviceName: selectedService?.name || service.serviceName,
          roomTypeId,
          roomType: selectedRoomType?.room_name || service.roomType,
          roomId,
          roomNumber: selectedRoom?.room_number || service.roomNumber,
          price: fallbackPrice,
          unit_price: fallbackPrice,
          tax_percentage: 0,
          gst_amount: 0,
          discount_amount: 0,
          sale_price: 0,
          surchargeDetails: [],
          surchargesTotal: 0,
          originalPrice: service.originalPrice ?? service.price,
          service_type_name: "boarding",
        };
        onUpdate(updatedService);
      })
      .finally(() => setPriceLoading(false));
  }, [
    clientId,
    pet?.id,
    serviceId,
    roomTypeId,
    service.checkin,
    service.checkout,
    nights,
    selectedService,
    selectedRoomType,
    selectedRoom,
  ]);

  // 🔥 Set isInitialMount to false after initial render (must be last effect)
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  const displayDate = `${new Date(service.checkin).toLocaleDateString("en-IN", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })} → ${new Date(service.checkout).toLocaleDateString("en-IN", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })} (${nights} nights)`;

  return (
    <CheckoutItemTable
      serviceType="boarding"
      service={service}
      pet={pet}
      getPetIcon={getPetIcon}
      serviceStatus={serviceStatus}
      isOrderClosed={isOrderClosed}
      priceLoading={priceLoading}
      nights={nights}
      clientId={clientId}
      serviceCategoryId={selectedService?.category_type_id}
      petTypeId={pet.pet_type_id}
      serviceTypeName="Boarding"
      surcharges={service.surchargeDetails || service.price_surcharge || []}
      onUpdateService={onUpdate}
      onViewActivity={onViewActivity}
      renderServiceSelector={() => (
        <div className="flex flex-col gap-1.5 w-full">
          {/* Row 1: Service + Room Type + Room Number in one line */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Service Select */}
            <select
              value={service.selectedServiceId || serviceId || ""}
              disabled={isOrderClosed}
              onChange={(e) => {
                const newServiceId = e.target.value;
                setServiceId(newServiceId);
                setRoomTypeId("");
                setRoomId("");
                onUpdate({
                  ...service,
                  service_id: newServiceId,
                  selectedServiceId: newServiceId,
                  room_type_id: "",
                  room_id: "",
                });
              }}
              className="text-xs sm:text-sm border border-gray-300 rounded-lg bg-white px-2.5 py-1 font-medium text-gray-800 focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">Select service</option>
              {boardingServices.map((opt) => (
                <option key={opt._id} value={opt._id}>
                  {opt.name} (₹
                  {Number(opt.mrp_price) || Number(opt.basic_price || 0)})
                </option>
              ))}
            </select>

            {/* Room Type Select */}
            <select
              value={roomTypeId || ""}
              onChange={(e) => {
                const newRoomTypeId = e.target.value;
                setRoomTypeId(newRoomTypeId);
                setRoomId("");
                onUpdate({
                  ...service,
                  room_type_id: newRoomTypeId,
                  room_id: "",
                });
              }}
              disabled={!serviceId || loadingRoomTypes || isOrderClosed}
              className="text-xs sm:text-sm border border-gray-300 rounded-lg bg-white px-2.5 py-1 font-medium text-gray-800 focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">
                {loadingRoomTypes ? "Loading..." : "Select room type"}
              </option>
              {roomTypes.map((opt) => (
                <option key={opt._id} value={opt._id}>
                  {opt.room_name}
                </option>
              ))}
            </select>

            {/* Room Number Select */}
            <select
              value={roomId || ""}
              onChange={(e) => {
                const newRoomId = e.target.value;
                setRoomId(newRoomId);
                onUpdate({ ...service, room_id: newRoomId });
              }}
              disabled={!roomTypeId || loadingRooms || isOrderClosed}
              className="text-xs sm:text-sm border border-gray-300 rounded-lg bg-white px-2.5 py-1 font-medium text-gray-800 focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">
                {loadingRooms ? "Loading..." : "Select room #"}
              </option>
              {availableRooms.map((opt) => (
                <option key={opt._id} value={opt._id}>
                  {opt.room_number}
                </option>
              ))}
            </select>
          </div>

          {/* Row 2: Check-out date picker + Date range in one line */}
          <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <span>Check‑out:</span>
              <input
                type="date"
                value={service.checkout?.slice(0, 10) || ""}
                min={service.checkin?.slice(0, 10)}
                disabled={isOrderClosed}
                onChange={(e) => {
                  const newCheckout = e.target.value;
                  onUpdate({ ...service, checkout: newCheckout });
                }}
                className="text-xs sm:text-sm border border-gray-300 rounded-lg px-2 py-0.5 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <span>
              {service.checkin ? new Date(service.checkin).toLocaleDateString() : ""} → {service.checkout ? new Date(service.checkout).toLocaleDateString() : ""} ({nights} night{nights > 1 ? "s" : ""})
            </span>
          </div>
        </div>
      )}
      renderMetaInfo={() => null}
    />
  );
};

export default BoardingCheckoutCard;
